/**
 * Serviço de Internacionalização (i18n)
 * Gerencia traduções, locale e formatação regionalizada
 */

import { detectLocale, getSupportedLocales, isLocaleSupported } from './detector.js';
import {
  REGIONAL_CONFIG,
  formatPrice,
  formatPriceCompact,
  getCurrencySymbol,
  getPayPalCurrency,
  getPricingForLocale
} from './pricing/regional-pricing.js';

class LocaleService {
  constructor() {
    this.LOCALE_KEY = 'summdash_locale';
    this.currentLocale = 'pt-BR'; // Default inicial
    this.translations = {};
    this.listeners = new Set();
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Inicializa o serviço de i18n
   * Detecta ou carrega locale salvo e carrega traduções
   */
  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInit();
    return this.initPromise;
  }

  async _doInit() {
    try {
      // Verificar locale salvo
      const savedLocale = localStorage.getItem(this.LOCALE_KEY);

      if (savedLocale && isLocaleSupported(savedLocale)) {
        this.currentLocale = savedLocale;
      } else {
        // Detectar locale baseado em IP + navegador
        this.currentLocale = await detectLocale();
        this.saveLocale(this.currentLocale);
      }

      // Carregar traduções
      await this.loadTranslations(this.currentLocale);

      this.initialized = true;
      this.applyLocale();

      return this.currentLocale;
    } catch {
      // Fallback para pt-BR
      this.currentLocale = 'pt-BR';
      await this.loadTranslations('pt-BR');
      this.initialized = true;
      return this.currentLocale;
    }
  }

  /**
   * Carrega arquivo de traduções para um locale
   * Fallback: pt-PT -> pt-BR, en-GB -> en-US
   * @param {string} locale
   */
  async loadTranslations(locale) {
    // Mapeamento de fallback para variantes regionais
    const fallbackMap = {
      'pt-PT': 'pt-BR',  // Portugal usa mesmas traduções do Brasil
      'en-GB': 'en-US'   // UK usa mesmas traduções dos US
    };

    try {
      // Tentar carregar locale exato primeiro
      const module = await import(`./translations/${locale}.js`);
      this.translations = module.default;
    } catch (error) {
      // Tentar fallback regional
      const fallbackLocale = fallbackMap[locale];
      if (fallbackLocale) {
        try {
          const fallbackModule = await import(`./translations/${fallbackLocale}.js`);
          this.translations = fallbackModule.default;
          return;
        } catch {
          // silent
        }
      }

      // Fallback final para pt-BR
      if (locale !== 'pt-BR') {
        const ptBR = await import('./translations/pt-BR.js');
        this.translations = ptBR.default;
      }
    }
  }

  /**
   * Salva preferência de locale
   * @param {string} locale
   */
  saveLocale(locale) {
    localStorage.setItem(this.LOCALE_KEY, locale);
  }

  /**
   * Aplica locale no documento
   */
  applyLocale() {
    document.documentElement.setAttribute('lang', this.currentLocale.split('-')[0]);
    document.documentElement.setAttribute('data-locale', this.currentLocale);
  }

  /**
   * Obtém tradução por chave (suporta notação de ponto)
   * @param {string} key - Ex: 'common.save' ou 'auth.login'
   * @param {Object} params - Parâmetros para interpolação
   * @returns {string} Texto traduzido ou a chave se não encontrada
   */
  t(key, params = {}) {
    let text = this._getNestedValue(this.translations, key);

    if (text === undefined) {
      return key;
    }

    // Interpolação de parâmetros {{param}}
    if (params && typeof text === 'string') {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(new RegExp(`{{${k}}}`, 'g'), v);
      });
    }

    return text;
  }

  /**
   * Obtém valor aninhado de um objeto usando notação de ponto
   * @private
   */
  _getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Altera o locale atual
   * @param {string} locale
   */
  async setLocale(locale) {
    if (!isLocaleSupported(locale)) {
      return false;
    }

    if (locale === this.currentLocale) {
      return true;
    }

    this.currentLocale = locale;
    this.saveLocale(locale);
    await this.loadTranslations(locale);
    this.applyLocale();
    this.notifyListeners();

    return true;
  }

  /**
   * Obtém locale atual
   * @returns {string}
   */
  getLocale() {
    return this.currentLocale;
  }

  /**
   * Obtém configuração regional atual
   * @returns {Object}
   */
  getRegionalConfig() {
    return REGIONAL_CONFIG[this.currentLocale] || REGIONAL_CONFIG['en-US'];
  }

  /**
   * Formata valor monetário no locale atual
   * @param {number} value
   * @param {Object} options
   * @returns {string}
   */
  formatCurrency(value, options = {}) {
    return formatPrice(value, this.currentLocale);
  }

  /**
   * Formata valor monetário de forma compacta
   * @param {number} value
   * @returns {string}
   */
  formatCurrencyCompact(value) {
    return formatPriceCompact(value, this.currentLocale);
  }

  /**
   * Obtém símbolo da moeda atual
   * @returns {string}
   */
  getCurrencySymbol() {
    return getCurrencySymbol(this.currentLocale);
  }

  /**
   * Obtém código da moeda atual (BRL, USD, EUR, etc)
   * @returns {string}
   */
  getCurrency() {
    const config = this.getRegionalConfig();
    return config.currency;
  }

  /**
   * Obtém código da moeda para PayPal
   * @returns {string}
   */
  getPayPalCurrency() {
    return getPayPalCurrency(this.currentLocale);
  }

  /**
   * Obtém preços formatados para o locale atual
   * @returns {Object}
   */
  getPricing() {
    return getPricingForLocale(this.currentLocale);
  }

  /**
   * Formata data no locale atual
   * @param {Date|string|number} date
   * @param {Object} options - Opções do Intl.DateTimeFormat
   * @returns {string}
   */
  formatDate(date, options = {}) {
    const d = date instanceof Date ? date : new Date(date);
    const config = this.getRegionalConfig();

    return d.toLocaleDateString(config.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...options
    });
  }

  /**
   * Formata data e hora no locale atual
   * @param {Date|string|number} date
   * @returns {string}
   */
  formatDateTime(date) {
    const d = date instanceof Date ? date : new Date(date);
    const config = this.getRegionalConfig();

    return d.toLocaleString(config.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Formata número no locale atual
   * @param {number} value
   * @param {Object} options
   * @returns {string}
   */
  formatNumber(value, options = {}) {
    const config = this.getRegionalConfig();
    return new Intl.NumberFormat(config.locale, options).format(value);
  }

  /**
   * Inscreve callback para mudanças de locale
   * @param {Function} callback
   * @returns {Function} Função para cancelar inscrição
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notifica todos os listeners sobre mudança de locale
   * @private
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentLocale);
      } catch {
        // silent
      }
    });
  }

  /**
   * Obtém lista de locales suportados
   * @returns {Array}
   */
  getSupportedLocales() {
    return getSupportedLocales();
  }

  /**
   * Verifica se está inicializado
   * @returns {boolean}
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Aguarda inicialização se necessário
   * @returns {Promise}
   */
  async waitForInit() {
    if (this.initialized) return this.currentLocale;
    return this.init();
  }
}

// Instância singleton
export const localeService = new LocaleService();

// Atalhos para uso comum
export const t = (key, params) => localeService.t(key, params);
export const formatCurrency = (value) => localeService.formatCurrency(value);
export const formatDate = (date, options) => localeService.formatDate(date, options);
export const formatDateTime = (date) => localeService.formatDateTime(date);
export const formatNumber = (value, options) => localeService.formatNumber(value, options);

// Re-exportar utilitários de pricing
export {
  REGIONAL_CONFIG,
  getPricingForLocale,
  getPayPalCurrency,
  getCurrencySymbol
} from './pricing/regional-pricing.js';

// Re-exportar utilitários de detecção
export { getSupportedLocales, isLocaleSupported } from './detector.js';
